import { useState, useEffect } from 'react';
import {
  FileText, Plus, Trash2, Copy, Check, ChevronDown, ChevronUp,
  ExternalLink, GripVertical, AlertCircle, X, ArrowUp, ArrowDown,
  Sparkles, Upload,
} from 'lucide-react';
import Layout from '../components/Layout';
import SubmissionsPanel from '../components/SubmissionsPanel';
import TagCreator from '../components/TagCreator';
import { PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { formsApi, platformConnectionsApi } from '../lib/api';
import type { Form, FormField, FormStyleConfig, FormRedirectConfig, FormSettings } from '../types/forms';
import type { PlatformConnection } from '../types/vault';

const CF_CONTACT_FIELDS = [
  { key: 'email_address', label: 'Email Address', required: true },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'time_zone', label: 'Time Zone' },
  { key: 'fb_url', label: 'Facebook URL' },
  { key: 'twitter_url', label: 'Twitter URL' },
  { key: 'instagram_url', label: 'Instagram URL' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'website_url', label: 'Website URL' },
] as const;


const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'nps', label: 'NPS (0–10)' },
  { value: 'rating', label: 'Star Rating' },
  { value: 'address', label: 'Address' },
  { value: 'image_upload', label: 'Image Upload' },
  { value: 'signature', label: 'Signature' },
  { value: 'hidden', label: 'Hidden' },
] as const;

// Field types that map to a single custom-attribute key rather than a CF standard field
const CUSTOM_ATTR_ONLY_TYPES: ReadonlyArray<FormField['type']> = ['image_upload', 'signature', 'address'];

const FONT_OPTIONS = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Arial', 'Georgia'];

function timeAgo(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function Forms() {
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [newFormName, setNewFormName] = useState('');
  const [newFormConnId, setNewFormConnId] = useState('');
  const [creating, setCreating] = useState(false);

  // Expanded form editing
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Form | null>(null);
  const [activeTab, setActiveTab] = useState<'fields' | 'import' | 'style' | 'redirects' | 'embed' | 'submissions'>('fields');
  const [saving, setSaving] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [cfTags, setCfTags] = useState<Array<{ id: number; name: string; color: string }>>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Import / AI builder
  const [importText, setImportText] = useState('');
  const [importDesc, setImportDesc] = useState('');
  const [importMode, setImportMode] = useState<'replace' | 'append'>('append');
  const [importError, setImportError] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);

  const loadForms = async () => {
    try {
      const res = await formsApi.list();
      if (res.success && res.data) {
        setForms((res.data as unknown as { forms: Form[] }).forms || []);
      }
    } catch (err) {
      setError('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      const res = await platformConnectionsApi.list();
      if (res.success && res.data) {
        const all = (res.data as unknown as { connections: PlatformConnection[] }).connections || [];
        setConnections(all.filter((c) => c.platform === 'clickfunnels' && c.status === 'active'));
      }
    } catch {}
  };

  useEffect(() => {
    loadForms();
    loadConnections();
  }, []);

  const handleCreate = async () => {
    if (!newFormName.trim() || !newFormConnId) return;
    setCreating(true);
    try {
      const res = await formsApi.create(newFormName.trim(), newFormConnId);
      if (res.success) {
        setShowCreateModal(false);
        setNewFormName('');
        setNewFormConnId('');
        loadForms();
      } else {
        setError(res.message || 'Failed to create form');
      }
    } catch {
      setError('Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this form and all its submissions?')) return;
    try {
      await formsApi.delete(id);
      setForms((prev) => prev.filter((f) => f.id !== id));
      if (expandedId === id) {
        setExpandedId(null);
        setEditForm(null);
      }
    } catch {
      setError('Failed to delete form');
    }
  };

  const handleExpand = async (formId: string) => {
    if (expandedId === formId) {
      setExpandedId(null);
      setEditForm(null);
      return;
    }
    setExpandedId(formId);
    setActiveTab('fields');
    try {
      const res = await formsApi.get(formId);
      if (res.success && res.data) {
        setEditForm((res.data as unknown as { form: Form }).form);
      }
    } catch {
      setError('Failed to load form');
    }
    loadTags(formId);
  };

  const handleSave = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      const cleanedFields = editForm.fields.map(f => f.type === 'select' ? { ...f, options: (f.options || []).filter(Boolean) } : f);
      const res = await formsApi.update(editForm.id, {
        name: editForm.name,
        fields: cleanedFields,
        styleConfig: editForm.styleConfig,
        redirectRules: editForm.redirectRules,
        settings: editForm.settings,
      });
      if (res.success && res.data) {
        const updated = (res.data as unknown as { form: Form }).form;
        setEditForm(updated);
        setForms((prev) => prev.map((f) => (f.id === updated.id ? { ...f, name: updated.name } : f)));
      }
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (form: Form) => {
    const newStatus = form.status === 'published' ? 'draft' : 'published';
    try {
      const res = await formsApi.update(form.id, { status: newStatus });
      if (res.success) {
        loadForms();
        if (editForm?.id === form.id) {
          setEditForm((prev) => prev ? { ...prev, status: newStatus } : prev);
        }
      }
    } catch {
      setError('Failed to update status');
    }
  };

  const loadTags = async (formId: string) => {
    setLoadingTags(true);
    try {
      const res = await formsApi.getTags(formId);
      if (res.success && res.data) {
        setCfTags((res.data as unknown as { tags: Array<{ id: number; name: string; color: string }> }).tags || []);
      }
    } catch {}
    setLoadingTags(false);
  };

  // Field management
  const addField = () => {
    if (!editForm) return;
    const newField: FormField = {
      id: crypto.randomUUID(),
      type: 'text',
      label: 'New Field',
      cfField: null,
      customAttributeKey: null,
      placeholder: '',
      required: false,
      options: [],
      sortOrder: editForm.fields.length,
    };
    setEditForm({ ...editForm, fields: [...editForm.fields, newField] });
  };

  const updateField = (fieldId: string, updates: Partial<FormField>) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      fields: editForm.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
    });
  };

  const removeField = (fieldId: string) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      fields: editForm.fields.filter((f) => f.id !== fieldId),
    });
  };

  const moveField = (fieldId: string, direction: 'up' | 'down') => {
    if (!editForm) return;
    const sorted = [...editForm.fields].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex((f) => f.id === fieldId);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === sorted.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const temp = sorted[idx].sortOrder;
    sorted[idx] = { ...sorted[idx], sortOrder: sorted[swapIdx].sortOrder };
    sorted[swapIdx] = { ...sorted[swapIdx], sortOrder: temp };
    setEditForm({ ...editForm, fields: sorted });
  };

  // ── Import / AI generation ──
  const buildAiPrompt = (): string => {
    const types = FIELD_TYPES.map((t) => t.value).join(', ');
    const cfKeys = CF_CONTACT_FIELDS.map((f) => f.key).join(', ');
    const desc = importDesc.trim() || '<describe your form here — e.g. "a customer feedback form with name, email, an NPS question, a 5-star rating, and their mailing address">';
    return `You are helping build a lead-capture form. Output ONLY a JSON array of field objects (no markdown, no commentary).

FORM TO BUILD:
${desc}

Each field object has these properties:
- "type": one of: ${types}
- "label": the visible field label (string)
- "required": true or false
- "placeholder": optional placeholder text (string, "" if none)
- "cfField": maps to a ClickFunnels standard contact field. Use one of: ${cfKeys}. Use null if it should be a custom attribute instead.
- "customAttributeKey": snake_case key for a ClickFunnels custom attribute (string), or null. Used when cfField is null.
- "options": for "select" type only — array of choice strings. Use [] otherwise.
- "displayMode": for "nps" type only — "buttons" or "slider".
- "maxRating": for "rating" type only — integer number of stars (default 5).

RULES:
- Always include an email field mapped to "email_address" (required).
- Use "nps" for 0–10 likelihood/recommendation questions (scored 0–10).
- Use "rating" for star ratings.
- Use "address" for a mailing/physical address (it renders street, city, state, ZIP, country sub-fields). Set customAttributeKey to a base like "address" and cfField to null.
- For "image_upload" and "signature", set cfField to null and provide a customAttributeKey.
- Map obvious contact fields (first name, last name, phone) to their cfField; everything else uses customAttributeKey.

EXAMPLE OUTPUT:
[
  {"type":"text","label":"First Name","required":true,"placeholder":"Jane","cfField":"first_name","customAttributeKey":null,"options":[]},
  {"type":"email","label":"Email","required":true,"placeholder":"jane@example.com","cfField":"email_address","customAttributeKey":null,"options":[]},
  {"type":"nps","label":"How likely are you to recommend us?","required":true,"placeholder":"","cfField":null,"customAttributeKey":"nps_score","options":[],"displayMode":"slider"},
  {"type":"rating","label":"Rate your experience","required":false,"placeholder":"","cfField":null,"customAttributeKey":"experience_rating","options":[],"maxRating":5},
  {"type":"address","label":"Mailing Address","required":false,"placeholder":"","cfField":null,"customAttributeKey":"address","options":[]}
]`;
  };

  const copyAiPrompt = () => {
    navigator.clipboard.writeText(buildAiPrompt());
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  };

  const normalizeImportedFields = (raw: unknown, startOrder: number): FormField[] => {
    if (!Array.isArray(raw)) throw new Error('Expected a JSON array of fields.');
    const allowedTypes = FIELD_TYPES.map((t) => t.value) as string[];
    const cfKeys = CF_CONTACT_FIELDS.map((f) => f.key) as string[];
    return raw.map((item: any, i: number): FormField => {
      const type: FormField['type'] = allowedTypes.includes(item?.type) ? item.type : 'text';
      const isCustomAttrOnly = CUSTOM_ATTR_ONLY_TYPES.includes(type);
      let cfField: string | null = null;
      let customAttributeKey: string | null = item?.customAttributeKey ? String(item.customAttributeKey) : null;
      if (!isCustomAttrOnly && item?.cfField && cfKeys.includes(item.cfField)) {
        cfField = item.cfField;
        customAttributeKey = null;
      }
      const field: FormField = {
        id: crypto.randomUUID(),
        type,
        label: typeof item?.label === 'string' && item.label.trim() ? item.label.trim() : 'Field',
        cfField,
        customAttributeKey,
        placeholder: typeof item?.placeholder === 'string' ? item.placeholder : '',
        required: Boolean(item?.required),
        options: Array.isArray(item?.options) ? item.options.map(String) : [],
        sortOrder: startOrder + i,
      };
      if (type === 'nps') field.displayMode = item?.displayMode === 'slider' ? 'slider' : 'buttons';
      if (type === 'rating') field.maxRating = Math.max(1, Math.min(10, Number(item?.maxRating) || 5));
      return field;
    });
  };

  const handleImportFields = async () => {
    if (!editForm) return;
    setImportError(null);
    setImportStatus(null);
    let parsed: unknown;
    try {
      let txt = importText.trim();
      // Tolerate fenced code blocks the AI may wrap the JSON in
      const fence = txt.match(/```(?:json)?\s*([\s\S]*?)```/i);
      if (fence) txt = fence[1].trim();
      parsed = JSON.parse(txt);
    } catch {
      setImportError('Could not parse JSON. Paste the array exactly as the AI produced it.');
      return;
    }
    try {
      // Accept either a bare array or an object wrapper like { "fields": [...] }.
      if (parsed && !Array.isArray(parsed) && typeof parsed === 'object' && Array.isArray((parsed as { fields?: unknown }).fields)) {
        parsed = (parsed as { fields: unknown }).fields;
      }
      const startOrder = importMode === 'append' ? editForm.fields.length : 0;
      const newFields = normalizeImportedFields(parsed, startOrder);
      if (newFields.length === 0) {
        setImportError('No fields found in the imported JSON.');
        return;
      }
      const merged = importMode === 'append' ? [...editForm.fields, ...newFields] : newFields;
      const updated = { ...editForm, fields: merged };
      setEditForm(updated);
      setImportStatus(`Imported ${newFields.length} field${newFields.length === 1 ? '' : 's'}.`);
      setImportText('');
      // Persist immediately
      const res = await formsApi.update(editForm.id, { fields: merged });
      if (!res.success) setImportError(res.message || 'Imported into the editor, but saving failed — click Save Changes.');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed.');
    }
  };

  const updateStyle = (key: keyof FormStyleConfig, value: string | boolean) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      styleConfig: { ...editForm.styleConfig, [key]: value },
    });
  };

  const updateRedirects = (updates: Partial<FormRedirectConfig>) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      redirectRules: { ...editForm.redirectRules, ...updates },
    });
  };

  const updateSettings = (updates: Partial<FormSettings>) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      settings: { ...editForm.settings, ...updates },
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <PageHeader
          eyebrow="Lead capture"
          title="Embeddable Forms"
          description="Create forms that save data directly to ClickFunnels"
          actions={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>Create Form</Button>}
        />

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
            <AlertCircle className="w-4 h-4" /> <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-auto text-ink-3 hover:text-danger"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Form list */}
        {forms.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No forms yet"
            description="Create your first embeddable form."
            action={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>Create Form</Button>}
          />
        ) : (
          <div className="space-y-3">
            {forms.map((form) => (
              <div
                key={form.id}
                className={`ds-card overflow-hidden ${expandedId === form.id ? '' : 'transition-all hover:-translate-y-0.5 hover:shadow-card'}`}
              >
                {/* Form row */}
                <div
                  className="flex items-center px-3 md:px-5 py-3 md:py-4 cursor-pointer hover:bg-surface-2"
                  onClick={() => handleExpand(form.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                      <span className="font-medium text-ink truncate">{form.name}</span>
                      <Badge tone={form.status === 'published' ? 'success' : 'neutral'}>{form.status}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-3">
                      <span className="truncate font-mono">/f/{form.slug}</span>
                      {form.connectionName && <span className="hidden sm:inline">via {form.connectionName}</span>}
                      <span>{form.submissionCount || 0} subs</span>
                      <span>{timeAgo(form.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {/* Publish toggle */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleTogglePublish(form); }}
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      style={{ background: form.status === 'published' ? 'var(--success)' : 'var(--ink-3)' }}
                      title={form.status === 'published' ? 'Unpublish' : 'Publish'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        form.status === 'published' ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(form.id); }}
                      className="p-1.5 text-ink-3 hover:text-danger"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedId === form.id ? <ChevronUp className="w-4 h-4 text-ink-3" /> : <ChevronDown className="w-4 h-4 text-ink-3" />}
                  </div>
                </div>

                {/* Expanded builder */}
                {expandedId === form.id && editForm && (
                  <div className="border-t border-line">
                    {/* Tabs */}
                    <div className="flex border-b border-line px-3 md:px-5 overflow-x-auto">
                      {(['fields', 'import', 'style', 'redirects', 'embed', 'submissions'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`px-3 md:px-4 py-3 text-xs md:text-sm font-medium border-b-2 -mb-px capitalize whitespace-nowrap ${
                            activeTab === tab
                              ? 'border-accent text-accent-ink'
                              : 'border-transparent text-ink-3 hover:text-ink-2'
                          }`}
                        >
                          {tab === 'import' ? 'Import / AI' : tab}
                        </button>
                      ))}
                    </div>

                    <div className="p-3 md:p-5">
                      {/* ── Fields Tab ── */}
                      {activeTab === 'fields' && (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <label className="ds-label">Form Name</label>
                              <input
                                type="text"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="ds-input w-full sm:w-64"
                              />
                            </div>
                            <Button variant="ghost" icon={Plus} onClick={addField}>Add Field</Button>
                          </div>

                          {editForm.fields.length === 0 ? (
                            <p className="text-ink-3 text-sm text-center py-8">No fields yet. Click "Add Field" to start building your form.</p>
                          ) : (
                            <div className="space-y-3">
                              {[...editForm.fields].sort((a, b) => a.sortOrder - b.sortOrder).map((field) => (
                                <div key={field.id} className="border border-line rounded-lg p-4 bg-surface-2">
                                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                                    {/* Reorder */}
                                    <div className="md:col-span-1 flex md:flex-col items-center gap-1 pt-1">
                                      <button onClick={() => moveField(field.id, 'up')} className="text-ink-3 hover:text-ink-2"><ArrowUp className="w-3.5 h-3.5" /></button>
                                      <GripVertical className="w-3.5 h-3.5 text-ink-3" />
                                      <button onClick={() => moveField(field.id, 'down')} className="text-ink-3 hover:text-ink-2"><ArrowDown className="w-3.5 h-3.5" /></button>
                                    </div>
                                    {/* Label */}
                                    <div className="md:col-span-3">
                                      <label className="text-xs text-ink-3 mb-1 block">Label</label>
                                      <input
                                        type="text"
                                        value={field.label}
                                        onChange={(e) => updateField(field.id, { label: e.target.value })}
                                        className="ds-input w-full"
                                      />
                                    </div>
                                    {/* Type */}
                                    <div className="md:col-span-2">
                                      <label className="text-xs text-ink-3 mb-1 block">Type</label>
                                      <select
                                        value={field.type}
                                        onChange={(e) => updateField(field.id, { type: e.target.value as FormField['type'] })}
                                        className="ds-input w-full"
                                      >
                                        {FIELD_TYPES.map((t) => (
                                          <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                    {/* CF Field Mapping */}
                                    <div className="md:col-span-3">
                                      <label className="text-xs text-ink-3 mb-1 block">
                                        {CUSTOM_ATTR_ONLY_TYPES.includes(field.type)
                                          ? (field.type === 'address' ? 'Custom Attribute Base Key' : 'Custom Attribute Key')
                                          : 'CF Field / Custom Attr'}
                                      </label>
                                      {CUSTOM_ATTR_ONLY_TYPES.includes(field.type) ? (
                                        <input
                                          type="text"
                                          value={field.customAttributeKey || ''}
                                          onChange={(e) => updateField(field.id, { customAttributeKey: e.target.value || null, cfField: null })}
                                          placeholder={field.type === 'signature' ? 'e.g. signature_url' : field.type === 'address' ? 'e.g. address' : 'e.g. profile_image_url'}
                                          className="ds-input w-full"
                                        />
                                      ) : (
                                        <select
                                          value={field.cfField || (field.customAttributeKey ? '__custom__' : '')}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '__custom__') {
                                              updateField(field.id, { cfField: null });
                                            } else if (val === '') {
                                              updateField(field.id, { cfField: null, customAttributeKey: null });
                                            } else {
                                              updateField(field.id, { cfField: val, customAttributeKey: null });
                                            }
                                          }}
                                          className="ds-input w-full"
                                        >
                                          <option value="">— Not mapped —</option>
                                          {CF_CONTACT_FIELDS.map((cf) => (
                                            <option key={cf.key} value={cf.key}>{cf.label}</option>
                                          ))}
                                          <option value="__custom__">Custom Attribute...</option>
                                        </select>
                                      )}
                                      {/* Show custom attribute key input when Custom Attribute is selected */}
                                      {!field.cfField && !CUSTOM_ATTR_ONLY_TYPES.includes(field.type) && (
                                        <input
                                          type="text"
                                          value={field.customAttributeKey || ''}
                                          onChange={(e) => updateField(field.id, { customAttributeKey: e.target.value || null })}
                                          placeholder="attribute_key"
                                          className="ds-input w-full mt-1"
                                        />
                                      )}
                                    </div>
                                    {/* Placeholder */}
                                    <div className="md:col-span-2">
                                      <label className="text-xs text-ink-3 mb-1 block">Placeholder</label>
                                      <input
                                        type="text"
                                        value={field.placeholder}
                                        onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                                        className="ds-input w-full"
                                      />
                                    </div>
                                    {/* Actions */}
                                    <div className="md:col-span-1 flex items-center gap-2 md:pt-5">
                                      <label className="flex items-center gap-1 text-xs text-ink-3" title="Required">
                                        <input
                                          type="checkbox"
                                          checked={field.required}
                                          onChange={(e) => updateField(field.id, { required: e.target.checked })}
                                          className="rounded"
                                        />
                                        Req
                                      </label>
                                      <button onClick={() => removeField(field.id)} className="text-ink-3 hover:text-danger">
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Options for select type */}
                                  {field.type === 'select' && (
                                    <div className="mt-3 ml-12">
                                      <label className="text-xs text-ink-3 mb-1 block">Options (one per line)</label>
                                      <textarea
                                        value={(field.options || []).join('\n')}
                                        onChange={(e) => updateField(field.id, { options: e.target.value.split('\n') })}
                                        className="ds-input w-full"
                                        rows={3}
                                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                                      />
                                    </div>
                                  )}
                                  {/* NPS display mode */}
                                  {field.type === 'nps' && (
                                    <div className="mt-3 ml-12">
                                      <label className="text-xs text-ink-3 mb-1 block">Display</label>
                                      <select
                                        value={field.displayMode || 'buttons'}
                                        onChange={(e) => updateField(field.id, { displayMode: e.target.value as 'buttons' | 'slider' })}
                                        className="ds-input w-40"
                                      >
                                        <option value="buttons">Buttons (0–10)</option>
                                        <option value="slider">Slider</option>
                                      </select>
                                      <p className="text-xs text-ink-3 mt-1">Stores a 0–10 score in the mapped field / custom attribute.</p>
                                    </div>
                                  )}
                                  {/* Star rating count */}
                                  {field.type === 'rating' && (
                                    <div className="mt-3 ml-12">
                                      <label className="text-xs text-ink-3 mb-1 block">Number of stars</label>
                                      <input
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={field.maxRating || 5}
                                        onChange={(e) => updateField(field.id, { maxRating: Math.max(1, Math.min(10, Number(e.target.value) || 5)) })}
                                        className="ds-input w-24"
                                      />
                                    </div>
                                  )}
                                  {/* Address hint */}
                                  {field.type === 'address' && (
                                    <div className="mt-3 ml-12">
                                      <p className="text-xs text-ink-3">Collects street, city, state, ZIP and country. Each component is saved as a separate ClickFunnels custom attribute (e.g. <code className="font-mono">address_city</code>) plus a combined <code className="font-mono">address</code> value.</p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Import / AI Tab ── */}
                      {activeTab === 'import' && (
                        <div className="space-y-6 max-w-3xl">
                          {/* Step 1: AI prompt */}
                          <div className="border border-line rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-4 h-4 text-accent-ink" />
                              <h3 className="font-display text-sm font-semibold text-ink">1. Generate fields with AI</h3>
                            </div>
                            <p className="text-xs text-ink-3 mb-3">
                              Describe your form, copy the prompt into ChatGPT / Claude / any AI, then paste the JSON it returns below.
                            </p>
                            <label className="text-xs font-medium text-ink-2 mb-1 block">Describe your form</label>
                            <textarea
                              value={importDesc}
                              onChange={(e) => setImportDesc(e.target.value)}
                              rows={3}
                              placeholder='e.g. "A customer feedback form: name, email, an NPS question as a slider, a 5-star rating, a comment box, and their mailing address."'
                              className="ds-input w-full mb-4"
                            />

                            {/* Full prompt preview — the definition + instructions, ready to copy into any AI */}
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs font-medium text-ink-2">Prompt to paste into your AI</label>
                              <Button variant="primary" icon={promptCopied ? Check : Copy} onClick={copyAiPrompt} className="!h-7 !px-2.5 !text-xs">
                                {promptCopied ? 'Copied!' : 'Copy Prompt'}
                              </Button>
                            </div>
                            <textarea
                              readOnly
                              value={buildAiPrompt()}
                              rows={14}
                              onFocus={(e) => e.currentTarget.select()}
                              className="ds-input w-full text-xs font-mono bg-surface-2 text-ink-2 resize-y"
                            />
                            <p className="text-xs text-ink-3 mt-1">
                              This includes the full field-type definition and rules. Edit the description above and the prompt updates automatically. Copy it, run it in ChatGPT / Claude / any AI, then paste the JSON result in step 2.
                            </p>
                          </div>

                          {/* Step 2: paste / import JSON */}
                          <div className="border border-line rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Upload className="w-4 h-4 text-accent-ink" />
                              <h3 className="font-display text-sm font-semibold text-ink">2. Paste &amp; import field JSON</h3>
                            </div>
                            <p className="text-xs text-ink-3 mb-3">
                              Paste the JSON array from the AI (or a field definition exported elsewhere). Code fences are handled automatically.
                            </p>
                            <textarea
                              value={importText}
                              onChange={(e) => { setImportText(e.target.value); setImportError(null); }}
                              rows={8}
                              placeholder='[ { "type": "email", "label": "Email", "required": true, "cfField": "email_address" }, ... ]'
                              className="ds-input w-full font-mono"
                            />
                            <div className="flex flex-wrap items-center gap-3 mt-3">
                              <label className="flex items-center gap-1.5 text-sm text-ink-2">
                                <input type="radio" name="import-mode" checked={importMode === 'append'} onChange={() => setImportMode('append')} />
                                Append to existing fields
                              </label>
                              <label className="flex items-center gap-1.5 text-sm text-ink-2">
                                <input type="radio" name="import-mode" checked={importMode === 'replace'} onChange={() => setImportMode('replace')} />
                                Replace all fields
                              </label>
                              <Button
                                variant="primary"
                                onClick={handleImportFields}
                                disabled={!importText.trim()}
                                className="ml-auto"
                              >
                                Import Fields
                              </Button>
                            </div>
                            {importError && (
                              <div className="mt-3 p-2.5 bg-danger-soft border border-line rounded text-danger text-xs flex items-center gap-2">
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {importError}
                              </div>
                            )}
                            {importStatus && (
                              <div className="mt-3 p-2.5 bg-success-soft border border-line rounded text-success text-xs flex items-center gap-2">
                                <Check className="w-3.5 h-3.5 flex-shrink-0" /> {importStatus} Switch to the <strong>Fields</strong> tab to review.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ── Style Tab ── */}
                      {activeTab === 'style' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <label className="ds-label">Primary Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editForm.styleConfig.primaryColor} onChange={(e) => updateStyle('primaryColor', e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editForm.styleConfig.primaryColor} onChange={(e) => updateStyle('primaryColor', e.target.value)} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Background Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editForm.styleConfig.backgroundColor} onChange={(e) => updateStyle('backgroundColor', e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editForm.styleConfig.backgroundColor} onChange={(e) => updateStyle('backgroundColor', e.target.value)} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Background Transparency</label>
                            <div className="flex items-center gap-3">
                              <input type="range" min="0" max="100" value={editForm.styleConfig.backgroundOpacity ?? 100} onChange={(e) => updateStyle('backgroundOpacity', Number(e.target.value))} className="flex-1 h-2 accent-[var(--accent)]" />
                              <span className="text-sm text-ink-3 w-10 text-right">{editForm.styleConfig.backgroundOpacity ?? 100}%</span>
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Text Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editForm.styleConfig.textColor} onChange={(e) => updateStyle('textColor', e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editForm.styleConfig.textColor} onChange={(e) => updateStyle('textColor', e.target.value)} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Font Family</label>
                            <select value={editForm.styleConfig.fontFamily} onChange={(e) => updateStyle('fontFamily', e.target.value)} className="ds-input w-full">
                              {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="ds-label">Button Text</label>
                            <input type="text" value={editForm.styleConfig.buttonText} onChange={(e) => updateStyle('buttonText', e.target.value)} className="ds-input w-full" />
                          </div>
                          <div>
                            <label className="ds-label">Border Radius</label>
                            <input type="text" value={editForm.styleConfig.borderRadius} onChange={(e) => updateStyle('borderRadius', e.target.value)} placeholder="8px" className="ds-input w-full" />
                          </div>
                          <div>
                            <label className="ds-label">Form Width</label>
                            <input type="text" value={editForm.styleConfig.formWidth} onChange={(e) => updateStyle('formWidth', e.target.value)} placeholder="480px" className="ds-input w-full" />
                          </div>
                          <div>
                            <label className="ds-label">Input Background</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editForm.styleConfig.inputBackgroundColor || '#FFFFFF'} onChange={(e) => updateStyle('inputBackgroundColor', e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editForm.styleConfig.inputBackgroundColor || '#FFFFFF'} onChange={(e) => updateStyle('inputBackgroundColor', e.target.value)} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Input Border Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editForm.styleConfig.inputBorderColor || '#D1D5DB'} onChange={(e) => updateStyle('inputBorderColor', e.target.value)} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editForm.styleConfig.inputBorderColor || '#D1D5DB'} onChange={(e) => updateStyle('inputBorderColor', e.target.value)} className="ds-input w-28" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-6">
                            <input type="checkbox" checked={editForm.styleConfig.showLabels} onChange={(e) => updateStyle('showLabels', e.target.checked)} className="rounded" id="show-labels" />
                            <label htmlFor="show-labels" className="text-sm text-ink-2">Show field labels</label>
                          </div>
                        </div>
                      )}

                      {/* ── Redirects Tab ── */}
                      {activeTab === 'redirects' && (
                        <div className="space-y-6">
                          <div>
                            <label className="ds-label">Default Redirect URL</label>
                            <input
                              type="url"
                              value={editForm.redirectRules.defaultRedirect}
                              onChange={(e) => updateRedirects({ defaultRedirect: e.target.value })}
                              placeholder="https://example.com/thank-you"
                              className="ds-input w-full"
                            />
                            <p className="text-xs text-ink-3 mt-1">Leave empty to show a success message instead of redirecting</p>
                          </div>

                          <div>
                            <label className="ds-label">Conditional Redirect Rules</label>
                            <div className="space-y-2">
                              {(editForm.redirectRules.rules || []).map((rule, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <select
                                    value={rule.condition}
                                    onChange={(e) => {
                                      const rules = [...(editForm.redirectRules.rules || [])];
                                      rules[idx] = { ...rules[idx], condition: e.target.value as 'has_image' | 'no_image' };
                                      updateRedirects({ rules });
                                    }}
                                    className="ds-input"
                                  >
                                    <option value="has_image">Has Image</option>
                                    <option value="no_image">No Image</option>
                                  </select>
                                  <span className="text-sm text-ink-3">&rarr;</span>
                                  <input
                                    type="url"
                                    value={rule.redirectUrl}
                                    onChange={(e) => {
                                      const rules = [...(editForm.redirectRules.rules || [])];
                                      rules[idx] = { ...rules[idx], redirectUrl: e.target.value };
                                      updateRedirects({ rules });
                                    }}
                                    placeholder="https://..."
                                    className="ds-input flex-1"
                                  />
                                  <button
                                    onClick={() => {
                                      const rules = (editForm.redirectRules.rules || []).filter((_, i) => i !== idx);
                                      updateRedirects({ rules });
                                    }}
                                    className="text-ink-3 hover:text-danger"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  const rules = [...(editForm.redirectRules.rules || []), { condition: 'has_image' as const, redirectUrl: '' }];
                                  updateRedirects({ rules });
                                }}
                                className="text-sm text-accent-ink hover:opacity-80"
                              >
                                + Add condition
                              </button>
                            </div>
                          </div>

                          <div className="border-t border-line pt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <input
                                type="checkbox"
                                checked={editForm.redirectRules.showSuccessMessage}
                                onChange={(e) => updateRedirects({ showSuccessMessage: e.target.checked })}
                                className="rounded"
                                id="show-success"
                              />
                              <label htmlFor="show-success" className="text-sm text-ink-2">Show success message (when no redirect)</label>
                            </div>
                            <input
                              type="text"
                              value={editForm.redirectRules.successMessage}
                              onChange={(e) => updateRedirects({ successMessage: e.target.value })}
                              placeholder="Thank you for your submission!"
                              className="ds-input w-full"
                            />
                          </div>

                          <div className="border-t border-line pt-4">
                            <label className="ds-label">Apply Tags on Submission</label>
                            <p className="text-xs text-ink-3 mb-3">Selected tags will be applied to the contact in ClickFunnels when the form is submitted. Use tags to trigger automations.</p>
                            {loadingTags ? (
                              <div className="flex items-center gap-2 text-sm text-ink-3">
                                <Spinner className="!h-4 !w-4" />
                                Loading tags...
                              </div>
                            ) : cfTags.length === 0 ? (
                              <p className="text-sm text-ink-3">No tags yet. Add one below — it'll be reusable across all your forms, quizzes, and buttons.</p>
                            ) : (
                              <div className="space-y-2 max-h-48 overflow-y-auto border border-line rounded-lg p-3">
                                {[...cfTags].sort((a, b) => a.name.localeCompare(b.name)).map((tag) => {
                                  const isSelected = (editForm.settings.tagIds || []).includes(String(tag.id));
                                  return (
                                    <label
                                      key={tag.id}
                                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${isSelected ? 'bg-accent-soft' : 'hover:bg-surface-2'}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {
                                          const current = editForm.settings.tagIds || [];
                                          const tagStr = String(tag.id);
                                          const updated = isSelected
                                            ? current.filter((t) => t !== tagStr)
                                            : [...current, tagStr];
                                          updateSettings({ tagIds: updated });
                                        }}
                                        className="rounded text-accent-ink"
                                      />
                                      <span
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: tag.color !== '#EEEEEE' ? tag.color : '#9CA3AF' }}
                                      />
                                      <span className="text-sm text-ink-2">{tag.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                            {!loadingTags && editForm.connectionId && (
                              <TagCreator
                                connectionId={editForm.connectionId}
                                onCreated={(tag) => {
                                  setCfTags((prev) => (prev.some((t) => t.id === tag.id) ? prev : [...prev, tag]));
                                  const cur = editForm.settings.tagIds || [];
                                  if (!cur.includes(String(tag.id))) updateSettings({ tagIds: [...cur, String(tag.id)] });
                                }}
                              />
                            )}

                            {(editForm.settings.tagIds || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {(editForm.settings.tagIds || []).map((tagId) => {
                                  const tag = cfTags.find((t) => String(t.id) === tagId);
                                  return tag ? (
                                    <span key={tagId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-soft text-accent-ink rounded text-xs">
                                      {tag.name}
                                      <button
                                        onClick={() => updateSettings({ tagIds: (editForm.settings.tagIds || []).filter((t) => t !== tagId) })}
                                        className="hover:opacity-70"
                                      >&times;</button>
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>

                        </div>
                      )}

                      {/* ── Embed Tab ── */}
                      {activeTab === 'embed' && (
                        <div className="space-y-6">
                          {editForm.status !== 'published' && (
                            <div className="p-3 bg-warning-soft border border-line rounded-lg text-warning text-sm flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              Publish this form to generate embed code and preview it.
                            </div>
                          )}

                          <div>
                            <label className="ds-label">Direct Link</label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 bg-surface-2 px-3 py-2 rounded text-sm font-mono text-ink truncate">
                                {formsApi.getFormUrl(editForm.slug)}
                              </code>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(formsApi.getFormUrl(editForm.slug));
                                  setCopiedLink(true);
                                  setTimeout(() => setCopiedLink(false), 2000);
                                }}
                                className="p-2 text-ink-3 hover:text-accent-ink"
                              >
                                {copiedLink ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                              </button>
                              {editForm.status === 'published' && (
                                <a
                                  href={formsApi.getFormUrl(editForm.slug)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-ink-3 hover:text-accent-ink"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="ds-label">CF Page Widget (Image Upload Only)</label>
                            {(() => {
                              const imgField = editForm.fields.find((f) => f.type === 'image_upload');
                              const attrKey = imgField?.customAttributeKey || '';
                              return (
                                <div className="mb-3">
                                  <p className="text-xs text-ink-3 mb-2">
                                    Add this script to a ClickFunnels page. It injects an image upload widget, reads the email field, uploads the image to ClickFunnels, and sets the image URL into a text field on the page.
                                    {!imgField && <span className="text-warning ml-1"> Add an Image Upload field in the Fields tab first.</span>}
                                  </p>
                                  <div className="bg-accent-soft border border-line rounded-lg p-3 text-xs text-ink-2 space-y-1.5">
                                    <p className="font-semibold">Setup on your ClickFunnels page:</p>
                                    <ol className="list-decimal ml-4 space-y-1">
                                      <li>Add a <strong>Text Input</strong> field to your CF form (this will hold the image URL)</li>
                                      <li>Set the field's <strong>CSS ID</strong> to <code className="bg-surface-2 px-1 rounded font-mono">image_url</code></li>
                                      <li>Set the field's <strong>Custom Type name</strong> to match your custom attribute (e.g. <code className="bg-surface-2 px-1 rounded font-mono">{attrKey || 'your_attribute_key'}</code>)</li>
                                      <li>Optionally hide the field by setting its visibility to hidden in CF's style settings</li>
                                      <li>Paste the script below into your page's <strong>Custom Code / Tracking</strong> section</li>
                                    </ol>
                                    <p className="text-accent-ink">The widget will automatically populate this field with the ClickFunnels image URL after upload.</p>
                                  </div>
                                </div>
                              );
                            })()}
                            <div className="relative">
                              <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 font-mono text-[12px] leading-relaxed overflow-auto">
                                {formsApi.getWidgetCode(editForm.slug)}
                              </pre>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(formsApi.getWidgetCode(editForm.slug));
                                  setCopiedEmbed(true);
                                  setTimeout(() => setCopiedEmbed(false), 2000);
                                }}
                                className="absolute top-2 right-2 p-2 bg-surface-2 rounded text-ink-3 hover:text-ink"
                              >
                                {copiedEmbed ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="ds-label">Full Form Embed (iframe)</label>
                            <div className="relative">
                              <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 font-mono text-[12px] leading-relaxed overflow-auto">
                                {formsApi.getEmbedCode(editForm.slug)}
                              </pre>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(formsApi.getEmbedCode(editForm.slug));
                                  setCopiedEmbed(true);
                                  setTimeout(() => setCopiedEmbed(false), 2000);
                                }}
                                className="absolute top-2 right-2 p-2 bg-surface-2 rounded text-ink-3 hover:text-ink"
                              >
                                {copiedEmbed ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          {editForm.status === 'published' && (
                            <div>
                              <label className="ds-label">Preview</label>
                              <div className="border border-line rounded-lg overflow-hidden bg-surface-2">
                                <iframe
                                  src={formsApi.getFormUrl(editForm.slug)}
                                  width="100%"
                                  height="500"
                                  style={{ border: 'none' }}
                                  title="Form Preview"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Submissions Tab ── */}
                      {activeTab === 'submissions' && (
                        <SubmissionsPanel kind="form" resourceId={form.id} />
                      )}

                      {/* Save button (fields/style/redirects tabs) */}
                      {['fields', 'style', 'redirects'].includes(activeTab) && (
                        <div className="mt-6 flex justify-end">
                          <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card w-full max-w-md shadow-card p-5">
              <h2 className="font-display text-lg font-semibold mb-4 text-ink">Create New Form</h2>
              <div className="space-y-4">
                <div>
                  <label className="ds-label">Form Name</label>
                  <input
                    type="text"
                    value={newFormName}
                    onChange={(e) => setNewFormName(e.target.value)}
                    placeholder="e.g. Contact Registration"
                    className="ds-input w-full"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="ds-label">ClickFunnels Connection</label>
                  {connections.length === 0 ? (
                    <p className="text-sm text-warning">No active ClickFunnels connections found. Add one in Vault &gt; Connections first.</p>
                  ) : (
                    <select
                      value={newFormConnId}
                      onChange={(e) => setNewFormConnId(e.target.value)}
                      className="ds-input w-full"
                    >
                      <option value="">Select connection...</option>
                      {connections.map((c) => (
                        <option key={c.id} value={c.id}>{c.displayName || c.workspaceName || c.platform}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => { setShowCreateModal(false); setNewFormName(''); setNewFormConnId(''); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || !newFormName.trim() || !newFormConnId}
                >
                  {creating ? 'Creating...' : 'Create Form'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
